// Normalized frontend auth-error contract (ADR-0012). One place maps a
// backend status + error code to an action, so pages never scatter status checks.

import { AuthErrorCode } from '@atlas/types'

/** Session-level auth error state kinds the UI can render safely. */
export enum AuthErrorStateKind {
	INACTIVE = 'inactive',
	NOT_PROVISIONED = 'not_provisioned',
	FORBIDDEN = 'forbidden',
}

/** Session-level auth error states the UI can render safely. */
export type AuthErrorState =
	| { kind: AuthErrorStateKind.INACTIVE }
	| { kind: AuthErrorStateKind.NOT_PROVISIONED }
	| { kind: AuthErrorStateKind.FORBIDDEN }

/** What the response interceptor should do for an auth error. */
export enum AuthActionType {
	RENEW = 'renew',
	CLEAR_SESSION = 'clear-session',
	ERROR_STATE = 'error-state',
	NONE = 'none',
}

/** What the response interceptor should do for an auth error. */
export type AuthAction =
	| { type: AuthActionType.RENEW } // 401 → attempt one OIDC renewal + single replay
	| { type: AuthActionType.CLEAR_SESSION; state: AuthErrorState } // 403 USER_INACTIVE → clear + inactive state
	| { type: AuthActionType.ERROR_STATE; state: AuthErrorState } // 403 ACCESS_FORBIDDEN / USER_NOT_PROVISIONED → preserve session
	| { type: AuthActionType.NONE } // network / 5xx / unknown-403 → do nothing (never clear valid state)

/** Extract the backend `error.code` from a response body, safely. */
export function extractErrorCode(data: unknown): string | undefined {
	if (data && typeof data === 'object' && 'error' in data) {
		const err = (data as { error?: unknown }).error
		if (err && typeof err === 'object' && 'code' in err) {
			const code = (err as { code?: unknown }).code
			if (typeof code === 'string') return code
		}
	}
	return undefined
}

/**
 * True for a network failure (no response) or a 5xx — the backend is unreachable
 * or broken, as opposed to a definitive 401/403 rejection of the request. Kept
 * separate from `classifyAuthResponse`'s `none` bucket (which also covers an
 * unrecognized 403 code) since only network/5xx should surface as "backend
 * unavailable" rather than an ordinary auth rejection.
 */
export function isBackendUnavailable(status: number | undefined): boolean {
	return status === undefined || status >= 500
}

/**
 * Classify an auth response. A generic `403` WITHOUT a recognized backend code is
 * intentionally `none` — it must not be treated as permission to clear the session.
 */
export function classifyAuthResponse(status: number | undefined, code: string | undefined): AuthAction {
	if (status === 401) return { type: AuthActionType.RENEW }
	if (status === 403) {
		if (code === AuthErrorCode.USER_INACTIVE) {
			return { type: AuthActionType.CLEAR_SESSION, state: { kind: AuthErrorStateKind.INACTIVE } }
		}
		if (code === AuthErrorCode.USER_NOT_PROVISIONED) {
			return { type: AuthActionType.ERROR_STATE, state: { kind: AuthErrorStateKind.NOT_PROVISIONED } }
		}
		if (code === AuthErrorCode.ACCESS_FORBIDDEN) {
			return { type: AuthActionType.ERROR_STATE, state: { kind: AuthErrorStateKind.FORBIDDEN } }
		}
		return { type: AuthActionType.NONE }
	}
	return { type: AuthActionType.NONE }
}
